import React, { JSX, useState } from 'react';
import { ImageHeader } from '../components/ImageHeader';
import faqsImage from 'url:../assets/rsvp-image.jpg';
import '../styles/FAQs.scss';

interface AccordionItem {
  id: string;
  question: string;
  answer: string | JSX.Element;
}

const FAQs = () => {
  const [openId, setOpenId] = useState<string | null>(null);

  const guestFaqs: AccordionItem[] = [
    {
      id: 'plus-one',
      question: 'Can I bring a plus-one?',
      answer: (<p>The barn can only hold so many people, so only those named on the invite are able to attend.</p>),
    },
    {
      id: 'children',
      question: 'Are children allowed?',
      answer: (<p>We love kids, however as the venue is tight for space, and a working farm, we’ve had to prioritise “babe in arm” situations.</p>),
    },
    {
      id: 'dress-code',
      question: 'What is the dress code?',
      answer: (
        <>
          <p>Honestly, we're flexible!</p>
          <p>If you’re looking for an excuse to get dressed to the nines, (<em>that thing in the back of your wardrobe</em>), please crack on. But the most important thing to us is that you’re there to have fun and you’re comfy.</p>
          <p>The bride isn’t wearing white, so have at it - the wedding party is in dark red and dark green.</p>
          <p>Those of you who are inclined to wear heels - this is a working farm, there’s some gravel and cobblestones, we’d suggest a chunky heel or a wedge, or bring trainers for the dancing.</p>
        </>
      ),
    },
    {
      id: 'photos',
      question: 'Is it okay to take pictures?',
      answer: (
        <>
          <p>During the party - yes, absolutely. We will create and share a Google Photos album for everyone to upload their pictures.</p>
          <p>For the ceremony, we’ve paid the wonderful Jess Brown to capture the moment so please pop your phone in your pocket and enjoy the show.</p>
        </>
      ),
    },
  ];

  const logisticsFaqs: AccordionItem[] = [
    {
      id: 'arrival-time',
      question: 'When should I arrive?',
      answer: (<p>Arrive from noon for a 1pm start - we will have fizz on arrival, and drinks and canapés after the ceremony.</p>),
    },
    {
      id: 'parking',
      question: 'What is the parking situation?',
      answer: (
        <>
          <p>There’s a whole field of parking available to you! If you want to drive up, rest assured there’s somewhere safe to leave the car.</p>
          <p>The field is on the right hand side and clearly marked as you approach the venue. Cars can be left overnight and collected on Sunday morning.</p>
        </>
      ),
    },
    {
      id: 'transportation',
      question: 'Can you recommend a taxi company?',
      answer: (
        <>
          We strongly recommend you pre-book a taxi back from the venue. Our venue have recommended <a href="https://www.prestigestroudprivatehire.com/" target="_blank" rel="noopener noreferrer">Prestige Stroud Taxi</a> and <a href="https://www.wonkyswheels.co.uk/" target="_blank" rel="noopener noreferrer">Wonky’s Wheels</a>.
          If you would like us to help you buddy up with people staying near you to share a cab, please let us know where you’re staying and we’ll put you in touch.
        </>
      ),
    },
    {
      id: 'ending',
      question: 'What time will the reception end?',
      answer: 'We’re kicking you out at 11.30pm!',
    },
    {
      id: 'venue',
      question: 'How can I learn more about your venue?',
      answer: (
        <>
          <p>Copsegrove is a magical place, and is much more than just a wedding venue.</p>
          <p>You can find out more about the farm and their activities <a href="https://www.copsegrovefarm.com/" target='_blank'>on their website.</a></p>
        </>
      ),
    },
  ];

  const foodFaqs: AccordionItem[] = [
    {
      id: 'dietary',
      question: 'What should I do if I have dietary restrictions?',
      answer: (
        <>
          Please make sure you’ve told us on <a href="https://forms.fillout.com/t/seeCp5rqzmus" target='_blank'>your RSVP</a>! Ian is amazing and can cook for pretty much any situation. If in doubt, reach out and confirm we have it.
        </>
      ),
    },
    {
      id: 'open-bar',
      question: 'Will there be an open bar?',
      answer: (
        <>
          We’ve stocked the bar so don’t worry about needing to bring cash for that. If you have any complaints, compliments or comments about the wine, please address those to the Mother of the Bride.
        </>
      ),
    },
    {
      id: 'registry',
      question: 'Where are you registered?',
      answer: (
        <>
          We have everything we could possibly need and more, so we’re not asking for gifts. We do have a <a href="/gifts" target="_blank">couple of charities</a> we love if you’d like to give to them, but we just appreciate you coming to join us on the day!
        </>
      ),
    },
  ];

  const travelFaqs: AccordionItem[] = [
    {
      id: 'where-stay',
      question: 'Where should I stay?',
      answer: (
        <>
          There are so many GREAT hotels and B&amp;Bs nearby, the venue is situated between Stroud, Bisley and Circencester. Some favourites suggested by the venue:<br />
          <ul>
            <li>The Painswick</li>
            <li>Calcot and Spa</li>
            <li>Burleigh Court Hotel</li>
            <li>The Crown - Frampton Mansell</li>
            <li>The Royal George - Birdlip</li>
            <li>The Bell Sapperton</li>
            <li>The Bear - Bisley</li>
            <li>The Stroud Hotel</li>
          </ul>
        </>
      ),
    },
    {
      id: 'how-to-get',
      question: 'What is the best way to get to the venue?',
      answer: (
        <>
          It’s by car only (unless you fancy a country walk in your gladrags). <br />
          <strong>whatthreewords</strong> location is: <a href="https://what3words.com/copiers.informs.increment" target="_blank" rel="noopener noreferrer">///copiers.informs.increment</a><br />
          If you’re using Satnav, use “Back Lane, Bisley, Stroud GL6 7BG” to make sure you’re heading to the right place. It’s at the end of a very, very long lane, don’t panic.
        </>
      ),
    },
  ];

  const otherFaqs: AccordionItem[] = [
    {
      id: 'cant-make-it',
      question: 'What should I do if I can’t make it?',
      answer: (<p>Just let us know as soon as you can, life takes over sometimes and we completely get it.</p>),
    },
    {
      id: 'hashtag',
      question: 'Is there a wedding hashtag?',
      answer: (<p>Oh god no.</p>),
    },
    {
      id: 'who-call',
      question: 'Whom should I call with questions?',
      answer: (
        <>
          Before the day? Either Erin or Luka<br />
          We will create a wedding WhatsApp group before the weekend for any last minute questions.
        </>
      ),
    },
  ];

  const toggleAccordion = (id: string) => {
    setOpenId(openId === id ? null : id);
  };

  const headerContent = <h1>FAQs</h1>;

  return (
    <>
      <ImageHeader headerContent={headerContent} imageUrl={faqsImage} />
      <article className="block-content block-content-longtext">
        <p>Here are some frequently asked questions to help you prepare for our celebration.</p>
      </article>
      <article className="block-content block-content-faq">
        <h2>Essential logistics & schedule</h2>
        <div className="accordion">
          {logisticsFaqs.map((item) => (
            <div key={item.id} className="accordion-item">
              <button
                className={`accordion-button ${openId === item.id ? 'accordion-button--open' : ''}`}
                onClick={() => toggleAccordion(item.id)}
                aria-expanded={openId === item.id}
                aria-controls={`${item.id}-content`}
              >
                <span className="accordion-question">{item.question}</span>
                <span className="accordion-icon">+</span>
              </button>
              <div
                id={`${item.id}-content`}
                className={`accordion-content ${openId === item.id ? 'accordion-content--open' : ''}`}
              >
                <div className="accordion-answer">
                  {item.answer}
                </div>
              </div>
            </div>
          ))}
        </div>
        <h2>Guest details</h2>
        <div className="accordion">
          {guestFaqs.map((item) => (
            <div key={item.id} className="accordion-item">
              <button
                className={`accordion-button ${openId === item.id ? 'accordion-button--open' : ''}`}
                onClick={() => toggleAccordion(item.id)}
                aria-expanded={openId === item.id}
                aria-controls={`${item.id}-content`}
              >
                <span className="accordion-question">{item.question}</span>
                <span className="accordion-icon">+</span>
              </button>
              <div
                id={`${item.id}-content`}
                className={`accordion-content ${openId === item.id ? 'accordion-content--open' : ''}`}
              >
                <div className="accordion-answer">
                  {item.answer}
                </div>
              </div>
            </div>
          ))}
        </div>
        <h2>Food & gifts</h2>
        <div className="accordion">
          {foodFaqs.map((item) => (
            <div key={item.id} className="accordion-item">
              <button
                className={`accordion-button ${openId === item.id ? 'accordion-button--open' : ''}`}
                onClick={() => toggleAccordion(item.id)}
                aria-expanded={openId === item.id}
                aria-controls={`${item.id}-content`}
              >
                <span className="accordion-question">{item.question}</span>
                <span className="accordion-icon">+</span>
              </button>
              <div
                id={`${item.id}-content`}
                className={`accordion-content ${openId === item.id ? 'accordion-content--open' : ''}`}
              >
                <div className="accordion-answer">
                  {item.answer}
                </div>
              </div>
            </div>
          ))}
        </div>
        <h2>Travel & Local information</h2>
        <div className="accordion">
          {travelFaqs.map((item) => (
            <div key={item.id} className="accordion-item">
              <button
                className={`accordion-button ${openId === item.id ? 'accordion-button--open' : ''}`}
                onClick={() => toggleAccordion(item.id)}
                aria-expanded={openId === item.id}
                aria-controls={`${item.id}-content`}
              >
                <span className="accordion-question">{item.question}</span>
                <span className="accordion-icon">+</span>
              </button>
              <div
                id={`${item.id}-content`}
                className={`accordion-content ${openId === item.id ? 'accordion-content--open' : ''}`}
              >
                <div className="accordion-answer">
                  {item.answer}
                </div>
              </div>
            </div>
          ))}
        </div>
        <h2>Other important questions</h2>
        <div className="accordion">
          {otherFaqs.map((item) => (
            <div key={item.id} className="accordion-item">
              <button
                className={`accordion-button ${openId === item.id ? 'accordion-button--open' : ''}`}
                onClick={() => toggleAccordion(item.id)}
                aria-expanded={openId === item.id}
                aria-controls={`${item.id}-content`}
              >
                <span className="accordion-question">{item.question}</span>
                <span className="accordion-icon">+</span>
              </button>
              <div
                id={`${item.id}-content`}
                className={`accordion-content ${openId === item.id ? 'accordion-content--open' : ''}`}
              >
                <div className="accordion-answer">
                  {item.answer}
                </div>
              </div>
            </div>
          ))}
        </div>
      </article>
    </>
  );
};

export default FAQs;
